/**
 * Shared parser for the "bare paginator" response convention used by the
 * Investors/Investments tags. The Swagger docs describe a flat top-level
 * shape:
 *   { current_page, data: [...], total, per_page, last_page, ... }
 * but the live backend actually returns Laravel's API Resource Collection
 * shape instead — pagination metadata nested under `meta`, plus a `links`
 * object, with `current_page`/`total`/etc. NOT present at the top level:
 *   { data: [...], links: {...}, meta: { current_page, total, per_page, last_page, ... } }
 * (confirmed live against GET /admin/users/{uuid}/investments — a 0-result
 * page still came back in this nested shape). Both are handled here: try
 * `response.meta` first, fall back to reading the same fields off the top
 * level for any endpoint that does match the flat shape the docs describe.
 * Neither of these is wrapped in `{ status, data: {...} }` (KYC's
 * convention) or `{ status, message, plans: [...] }` (Investment Plans'
 * convention) — this deliberately isn't reused for those other tags, and
 * their parsers don't reuse this one.
 *
 * Never throws: an unrecognized shape logs a console.error with the raw
 * response and returns an empty, `parseError: true` page instead — a
 * shape mismatch on a successful HTTP response is a parsing bug, not a
 * network failure, and the two should never look the same to the UI (see
 * kycApi.js's unwrapSubmissionsPage for the incident that established this
 * pattern).
 */
export function parseLaravelPaginator(response, { context = 'paginator' } = {}) {
  if (response && Array.isArray(response.data)) {
    // Prefer the nested `meta` object when present — that's what the live
    // backend actually sends, even though the docs show these fields at
    // the top level. Fall back to the top level for anything that does
    // match the documented flat shape.
    const meta = response.meta && typeof response.meta === 'object' ? response.meta : response;
    return {
      items: response.data,
      meta: {
        currentPage: meta.current_page ?? 1,
        lastPage: meta.last_page ?? 1,
        perPage: meta.per_page ?? response.data.length,
        total: meta.total ?? response.data.length,
      },
    };
  }

  // Fallback: tolerate a bare array too, in case some route ever returns
  // an unpaginated list.
  if (Array.isArray(response)) {
    return { items: response, meta: { currentPage: 1, lastPage: 1, perPage: response.length, total: response.length } };
  }

  // eslint-disable-next-line no-console
  console.error(`[${context}] Unexpected paginator response shape — expected a top-level { data: [...] } array, with pagination fields either alongside it or nested under \`meta\`. Got:`, response);
  return { items: [], meta: { currentPage: 1, lastPage: 1, perPage: 0, total: 0 }, parseError: true };
}

export default parseLaravelPaginator;
